import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { errorView } from '@krp/client-runtime'

import { verifyPosCashier } from '../api/cashierAccessCommand'
import { aqsiSessionOpen } from '../api/rmkApi'
import { sameId } from '../../../shared/api/wire'
import { storeDispatch } from '../../../shared/model/dispatch'
import { newCommandIntent } from '../../../shared/model/ids'
import { ownCashierGrantRequested } from './ownSignIn'
import type { OwnWorker } from './types'

/**
 * Перенос рабочего места в эту вкладку (RP-CA5): код кассира → служба подтверждает его → `session/open` того же кассира. Решает сервер:
 * он же и отказывает, если код не тот. Результат — слова отказа или `null`; новое состояние окно читает само (теги `session/open`).
 * Код живёт только в замыкании: нигде не хранится. Работа под своим именем кода не имеет — идёт без него.
 *
 * Код проверяется тем же способом, каким входят в «Начале работы» (К-120): `own` — кассир нашей системы, связанный с кассиром работы, — его
 * личный код идёт в службу кассиров (`people/sign-in` + `people/grants`); без `own` — код кассира кабинета AQSI (`verify`).
 */
export function workplaceMoveRequested({
  resourceId,
  deviceId,
  cashierId,
  credential,
  own,
}: {
  resourceId: string
  deviceId: number
  cashierId: string | null
  credential: string
  own: OwnWorker | null
}): ThunkAction<Promise<string | null>, unknown, unknown, UnknownAction> {
  return async (dispatch, getState, extra) => {
    let cashierProof: string | undefined
    if (cashierId !== null) {
      if (credential.trim() === '') return 'Введите код кассира.'
      if (own !== null) {
        // Пропуск выдаётся на кассира источника из связи: он обязан быть кассиром этой работы, иначе служба кассы откажет уже после проверки кода.
        if (!sameId(own.sourceCashierId, cashierId)) return 'Этот кассир нашей системы не связан с кассиром открытой работы.'
        const granted = await ownCashierGrantRequested({ resourceId, worker: own, credential })(dispatch, getState, extra)
        if (!granted.ok) return granted.message
        cashierProof = granted.grant
      } else {
        try {
          const verified = await verifyPosCashier({ resourceId, sourceCashierId: cashierId, credential, purpose: 'pos' })(dispatch, getState, extra)
          if (!verified.ok) return errorView(verified.error).message
          cashierProof = verified.data.grant
        } catch (error) {
          return errorView(error).message
        }
      }
    }
    try {
      await storeDispatch(dispatch)(
        aqsiSessionOpen.initiate(
          {
            resourceId,
            deviceId,
            intent: newCommandIntent(),
            ...(cashierId !== null ? { cashierId } : { asSelf: true }),
            ...(cashierProof !== undefined ? { cashierProof } : {}),
          },
          { track: false },
        ),
      ).unwrap()
      return null
    } catch (error) {
      return errorView(error).message
    }
  }
}
